import {
  PLATFORM_VARIABLES,
  PlatformAutomationDraft,
  PlatformAutomationStep,
  SendEmailStep,
  TimelineItem,
} from "../types";
import {
  buildDelay,
  delayToMinutes,
  formatDelayLabel,
  formatTimelineOffset,
  type DelayUnit,
} from "./delay.utils";

export const createStepId = (prefix: string) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

export const normalizeDraft = (
  raw: Record<string, unknown> | undefined
): PlatformAutomationDraft => {
  const draft = (raw || {}) as PlatformAutomationDraft;
  const sorted = [...(draft.steps || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const seenIds = new Set<string>();
  const steps = sorted.map((step, index) => {
    let id = step.id;
    if (!id || seenIds.has(id)) {
      id = createStepId(step.type === "delay" ? "delay" : "email");
    }
    seenIds.add(id);
    return { ...step, id, order: index + 1 };
  });
  return {
    trigger: draft.trigger || "user_email_verified",
    enrollmentPolicy: draft.enrollmentPolicy || "once_per_user",
    defaultLocale: draft.defaultLocale || "es",
    defaultTimezone: draft.defaultTimezone || "America/Santiago",
    welcomeStepPolicy:
      draft.welcomeStepPolicy || "respect_platform_welcome_owner",
    conditions: draft.conditions || {
      all: [{ field: "freeTrial", operator: "eq", value: true }],
    },
    stopRules: draft.stopRules || [
      { event: "converted", stopScope: "automation" },
      { event: "unsubscribed", stopScope: "channel", channel: "email" },
    ],
    steps,
  };
};

export const draftToJson = (draft: PlatformAutomationDraft) =>
  JSON.stringify(draft, null, 2);

export const parseDraftJson = (json: string): PlatformAutomationDraft => {
  const parsed = JSON.parse(json) as Record<string, unknown>;
  return normalizeDraft(parsed);
};

export const reindexSteps = (
  steps: PlatformAutomationStep[]
): PlatformAutomationStep[] =>
  steps.map((step, index) => ({ ...step, order: index + 1 }));

export const moveStep = (
  steps: PlatformAutomationStep[],
  fromIndex: number,
  toIndex: number
): PlatformAutomationStep[] => {
  if (toIndex < 0 || toIndex >= steps.length) return steps;
  const next = [...steps];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return reindexSteps(next);
};

export const createDelayStep = (
  amount = 1,
  unit: DelayUnit = "days"
): PlatformAutomationStep => ({
  id: createStepId("delay"),
  type: "delay",
  order: 0,
  delay: buildDelay(amount, unit),
});

export const createEmailStep = (
  defaultLocale = "es"
): PlatformAutomationStep => ({
  id: createStepId("email"),
  type: "send_email",
  order: 0,
  topic: "platform_onboarding",
  sendAtTime: "10:00",
  deliveryTiming: "immediate",
  defaultLocale,
  templatesByLocale: {},
});

export const isFirstSendEmailStep = (
  steps: PlatformAutomationStep[],
  index: number
): boolean => {
  if (steps[index]?.type !== "send_email") return false;
  return steps.findIndex((s) => s.type === "send_email") === index;
};

export const computeTimeline = (
  steps: PlatformAutomationStep[],
  templatesById: Record<number, { name: string; subject?: string }> = {}
): TimelineItem[] => {
  let offsetMinutes = 0;
  const items: TimelineItem[] = [];
  let firstEmailSeen = false;

  steps.forEach((step) => {
    if (step.type === "delay") {
      offsetMinutes += delayToMinutes(step.delay);
      const offsetLabel = formatTimelineOffset(offsetMinutes);
      items.push({
        stepId: step.id,
        type: "delay",
        dayOffset: Math.floor(offsetMinutes / (24 * 60)),
        offsetMinutes,
        offsetLabel,
        label: `Esperar ${formatDelayLabel(step.delay)}`,
        detail: `Acumulado: ${offsetLabel}`,
      });
      return;
    }

    const emailStep = step as SendEmailStep;
    const locale = emailStep.defaultLocale || "es";
    const ref = emailStep.templatesByLocale?.[locale];
    const templateName = ref?.templateId
      ? templatesById[ref.templateId]?.name ||
        templatesById[ref.templateId]?.subject ||
        `Template #${ref.templateId}`
      : "Sin template";

    const isFirstEmail = !firstEmailSeen;
    firstEmailSeen = true;
    const timingDetail =
      isFirstEmail &&
      emailStep.deliveryTiming === "next_scheduled_time"
        ? `A las ${emailStep.sendAtTime || "10:00"}`
        : isFirstEmail
          ? "Inmediato"
          : "Según espera anterior";

    items.push({
      stepId: step.id,
      type: "send_email",
      dayOffset: Math.floor(offsetMinutes / (24 * 60)),
      offsetMinutes,
      offsetLabel: formatTimelineOffset(offsetMinutes),
      label: `Enviar email — ${templateName}`,
      detail: `${timingDetail} · ${locale}`,
    });
  });

  return items;
};

export const extractVariables = (subject = "", html = ""): string[] => {
  const found = new Set<string>();
  const text = `${subject} ${html}`;
  const matches = text.match(/\{\{[^}]+\}\}/g) || [];
  matches.forEach((token) => {
    const key = token.replace(/[{}]/g, "").trim();
    if (key) found.add(key);
  });
  PLATFORM_VARIABLES.forEach((v) => {
    if (text.includes(`{{${v}}}`)) found.add(v);
  });
  return Array.from(found);
};

export const stripHtmlToText = (html: string) =>
  html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Usa la versión publicada más reciente de cada template al enviar pruebas */
export const hydrateStepTemplatesForTest = (
  step: PlatformAutomationStep,
  templates: Array<{ id: number; publishedVersionId?: number }>
): PlatformAutomationStep => {
  if (step.type !== "send_email") return step;

  const emailStep = step as SendEmailStep;
  const templatesByLocale = { ...(emailStep.templatesByLocale || {}) };

  Object.entries(templatesByLocale).forEach(([locale, ref]) => {
    if (!ref?.templateId) return;
    const template = templates.find((item) => item.id === ref.templateId);
    if (template?.publishedVersionId) {
      templatesByLocale[locale] = {
        ...ref,
        templateVersionId: template.publishedVersionId,
      };
    }
  });

  return { ...emailStep, templatesByLocale };
};

export const hydrateDraftStepsForTest = (
  steps: PlatformAutomationStep[] = [],
  templates: Array<{ id: number; publishedVersionId?: number }>
): PlatformAutomationStep[] =>
  steps.map((step) => hydrateStepTemplatesForTest(step, templates));
